import React, { useState, useEffect, useCallback } from 'react';
import { Bell, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';

export const NotificationsDropdown = ({ unreadCount, onUpdate }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const fetchNotifications = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get('/notifications');
      setNotifications(response.data);
      onUpdate();
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [onUpdate]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await api.patch(`/notifications/${notificationId}/read`);
      // fetchNotifications already calls onUpdate — calling it here too ran
      // the Layout unread-count refresh twice per click (perf review P28).
      fetchNotifications();
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    if (notification.reference_type === 'parts_request' && notification.reference_id) {
      navigate('/parts-inbox');
    } else if (notification.reference_type === 'estimate' && notification.reference_id) {
      navigate('/estimates');
    } else if (notification.reference_type === 'task' && notification.reference_id) {
      navigate('/dashboard');
    } else if (notification.reference_type === 'blog') {
      // Blog post reminder (services/blog_reminder.py) — no reference_id: the
      // nudge is about the blog itself, not one post. Lands on the editor.
      navigate('/marketing/blog');
    } else if (notification.reference_type === 'feed_review') {
      // Feed lifecycle alerts (drops / archived-still-in-feed) — no
      // reference_id required: the drop alert is an aggregate.
      navigate('/vehicles/feed-review');
    } else if (notification.reference_type === 'ai_models') {
      // Platform AI model lifecycle alert (services/ai_model_lifecycle.py) —
      // SuperAdmins only; lands on the AI Models page to test a replacement.
      navigate('/admin/ai-models');
    } else if (notification.reference_type === 'vehicle' && notification.reference_id) {
      // One car — a paint colour to confirm (the banner on its page asks), a
      // feed-created car, a feed stock conflict. These used to do nothing.
      navigate(`/vehicles/${notification.reference_id}`);
    }
    
    setIsOpen(false);
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        data-testid="notifications-button"
        className="relative p-2 hover:bg-accent rounded-lg transition-colors"
      >
        <Bell className="h-5 w-5" />
        {unreadCount > 0 && (
          <span className="absolute top-1 right-1 h-2 w-2 bg-red-600 rounded-full"></span>
        )}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)}></div>
          <div className="absolute right-0 top-full mt-2 w-80 md:w-96 bg-card border border-border rounded-lg shadow-lg z-50 max-h-[80vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h3 className="font-semibold text-sm">Notifications</h3>
              <button
                onClick={() => setIsOpen(false)}
                className="p-1 hover:bg-accent rounded transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="overflow-y-auto flex-1">
              {loading ? (
                <div className="p-8 text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                </div>
              ) : notifications.length === 0 ? (
                <div className="p-8 text-center text-sm text-muted-foreground">
                  No notifications
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {notifications.map((notif) => (
                    <button
                      key={notif.id}
                      onClick={() => handleNotificationClick(notif)}
                      className={`w-full p-4 text-left hover:bg-accent transition-colors ${
                        !notif.read ? 'bg-blue-50 dark:bg-blue-900' : ''
                      }`}
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <p className="font-medium text-sm">{notif.title}</p>
                        <span className="text-xs text-muted-foreground whitespace-nowrap">
                          {formatTime(notif.created_at)}
                        </span>
                      </div>
                      <p className="text-xs text-muted-foreground">{notif.message}</p>
                      {!notif.read && (
                        <span className="inline-block mt-2 w-2 h-2 bg-blue-600 rounded-full"></span>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};